import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Box, Typography, Grid, Card, TextField, Button, Chip, MenuItem
} from '@mui/material';
import {
  Handshake, Send, CheckCircle, TrendingUp, Groups, Support
} from '@mui/icons-material';
import WebsiteLayout from '../../components/Website/WebsiteLayout';
import HeroSection from '../../components/Website/HeroSection';
import SEO from '../../components/SEO/SEO';
import { getPageSEO } from '../../config/seo';
import api from '../../services/api';
import PhoneInput from '../../components/shared/PhoneInput';
import { useRegion } from '../../context/RegionContext';
import { useSnackbar } from 'notistack';
const businessTypes = [
  'Accounting Firm',
  'IT / Software Agency',
  'Business Consultancy',
  'Value Added Reseller (VAR)',
  'Independent Professional',
  'Other'
];

const BecomePartnerPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { phonePrefix } = useRegion();
  const seo = getPageSEO('partner');
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    country: '',
    countryCode: phonePrefix,
    phone: '',
    businessType: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const fullPhone = `${formData.countryCode} ${formData.phone}`;
      const response = await api.post('/partners', { ...formData, phone: fullPhone });

      if (response.data.success) {
        enqueueSnackbar('Partner inquiry submitted successfully! We will contact you soon.', { variant: 'success' });
        setFormData({
          name: '',
          contactPerson: '',
          email: '',
          country: '',
          countryCode: formData.countryCode,
          phone: '',
          businessType: '',
          message: ''
        });
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to submit inquiry', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <TrendingUp />,
      title: 'Revenue Growth',
      desc: 'Earn competitive commissions on every successful referral and implementation'
    },
    {
      icon: <Support />,
      title: 'Dedicated Support',
      desc: 'Get priority technical support and dedicated partner success manager'
    },
    {
      icon: <Groups />,
      title: 'Co-Marketing',
      desc: 'Access to marketing materials, co-branded content, and joint campaigns'
    },
    {
      icon: <CheckCircle />,
      title: 'Training & Certification',
      desc: 'Comprehensive training programs and partner certification opportunities'
    }
  ];

  return (
    <WebsiteLayout>
      <SEO {...seo} />
      <HeroSection chip="Partner Program">
        <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h1" sx={{ fontSize: { xs: 40, md: 56 }, fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
              Become a FluxERP Partner
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.95, color: 'primary.contrastText' }}>
              Join our growing partner ecosystem and help businesses transform their operations
            </Typography>
          </Box>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 12 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 800, mb: 2 }}>
            Partner Benefits
          </Typography>
          <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 700, mx: 'auto' }}>
            Unlock exclusive benefits and grow your business with FluxERP
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 12 }}>
          {benefits.map((benefit, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(22, 51, 0, 0.12)',
                  bgcolor: theme => theme.palette.mode === 'dark' ? 'background.paper' : 'white',
                  boxShadow: theme => theme.palette.mode === 'dark' ? 'none' : '0 4px 20px rgba(22, 51, 0, 0.08)',
                  transition: 'all 0.3s',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme => theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(22, 51, 0, 0.12)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.contrastText',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {benefit.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {benefit.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {benefit.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
              Partner With Us
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: 18 }}>
              Fill out the form and our partnership team will get back to you within 24 hours.
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company / Individual Name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contact Person Name"
                    required
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Country"
                    required
                    value={formData.country || ''}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  >
                    <MenuItem value="GB">United Kingdom (GBP)</MenuItem>
                    <MenuItem value="US">United States (USD)</MenuItem>
                    <MenuItem value="EU">European Union (EUR)</MenuItem>
                    <MenuItem value="AU">Australia (AUD)</MenuItem>
                    <MenuItem value="PK">Pakistan (PKR)</MenuItem>
                    <MenuItem value="AE">UAE (AED)</MenuItem>
                    <MenuItem value="SA">Saudi Arabia (SAR)</MenuItem>
                    <MenuItem value="QA">Qatar (QAR)</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PhoneInput
                    countryCode={formData.countryCode}
                    phoneNumber={formData.phone}
                    onCountryCodeChange={(code) => setFormData({ ...formData, countryCode: code })}
                    onPhoneNumberChange={(phone) => setFormData({ ...formData, phone })}
                    label="Phone Number"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    label="Business Type"
                    required
                    value={formData.businessType}
                    onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                  >
                    {businessTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message / Notes"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    endIcon={<Send />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: 16,
                      fontWeight: 700,
                      bgcolor: 'primary.contrastText', // Forest Green
                      color: 'primary.main', // Bright Green
                      boxShadow: '0 4px 20px rgba(22, 51, 0, 0.15)',
                      '&:hover': {
                        bgcolor: 'secondary.dark',
                        boxShadow: '0 6px 24px rgba(22, 51, 0, 0.2)'
                      }
                    }}
                  >
                    {loading ? 'Submitting...' : 'Submit Inquiry'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                px: 4,
                py: 6,
                height: '100%',
                bgcolor: 'secondary.main',
                color: 'white'
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, color: 'white' }}>
                Why Partner With FluxERP?
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    💼 Proven Platform
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    Join a trusted platform used by businesses worldwide for accounting and ERP needs.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    🚀 Growth Opportunities
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    Expand your service offerings and tap into new revenue streams.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    🤝 Collaborative Success
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    Work with a dedicated team committed to your success and growth.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                    📚 Comprehensive Resources
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, color: 'white' }}>
                    Access training materials, technical documentation, and marketing assets.
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', py: 12, mb: 0 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Handshake sx={{ fontSize: 64, mb: 3, color: 'primary.contrastText' }} />
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 3, color: 'primary.contrastText' }}>
              Ready to Partner?
            </Typography>
            <Typography variant="h6" sx={{ color: 'primary.contrastText', mb: 4, opacity: 0.9 }}>
              Join our partner program today and start growing your business with FluxERP
            </Typography>
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              sx={{
                bgcolor: 'primary.contrastText', // Forest Green
                color: 'primary.main', // Bright Green
                px: 4,
                py: 2,
                borderRadius: 2,
                fontWeight: 700,
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: 'secondary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(22, 51, 0, 0.25)'
                }
              }}
            >
              Join Partner Program
            </Button>
          </Box>
        </Container>
      </Box>
    </WebsiteLayout>
  );
};

export default BecomePartnerPage;
